import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ButtonBase } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { primitiveColors, semanticColors } from '@/theme/tokens';

const HIGHLIGHT_COLOR = '#CED8E2';

export type YourPicksFilterValue = 'picks' | 'all';

interface YourPicksFilterProps {
  value: YourPicksFilterValue;
  onToggle: () => void;
  /**
   * When true, clicks bubble up to the parent AccordionSummary instead of
   * toggling. Only used for the no-hearts collapsed case, where the original
   * spec's "click anywhere on a collapsed header expands and resets to All"
   * behavior is preserved. When the contest has hearts, the parent toggles
   * and expands directly.
   */
  bubbleClick?: boolean;
  /**
   * Whether the contest has any liked candidates. When `false` and `value` is
   * `'picks'`, the heart icon stays outlined (instead of filling red) — signals
   * a "no picks yet" state without changing the pill position.
   */
  hasHearts?: boolean;
  disabled?: boolean;
}

/**
 * Two-state pill toggle for filtering candidates inside a contest accordion.
 * Controlled — `value`/`onToggle` are owned by the parent so the filter state
 * can interact with the accordion's expansion state (auto-collapse when there
 * are no liked candidates, reset on re-expand).
 *
 * Layout mirrors the partisan selector: each side is `flex: 1`, so the pill
 * is a static 50% slide. No JS measurement needed — the heart-only side and
 * the "All" side are close enough in natural width that an even split looks
 * fine.
 */
export const YourPicksFilter: React.FC<YourPicksFilterProps> = ({
  value,
  onToggle,
  bubbleClick = false,
  hasHearts = true,
  disabled = false,
}) => {
  const { t } = useTranslation('ballot');

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (bubbleClick) {
        // No-hearts collapsed path: let the click bubble to the AccordionSummary
        // so its expand handler runs (and resets the filter to 'all' if needed).
        return;
      }
      // Otherwise the parent's `onToggle` flips the filter and force-expands
      // the accordion in one step — so a click on a collapsed header (with
      // hearts) both toggles and opens.
      e.stopPropagation();
      onToggle();
    },
    [bubbleClick, onToggle]
  );

  const isPicks = value === 'picks';

  return (
    <ButtonBase
      // Render as a div, not a button — this control sits inside a
      // contest AccordionSummary which is already a ``<button>``.
      // Nested ``<button>``s are invalid HTML; React's dev-mode
      // validator logs the entire JSX tree on every render when this
      // happens, which dominates the perf trace. ``role="button"`` +
      // ButtonBase's built-in keydown handling preserves a11y.
      component="div"
      role="button"
      disableRipple
      disabled={disabled}
      onClick={handleClick}
      aria-label={isPicks ? t('contests.filterShowingPicks') : t('contests.filterShowingAll')}
      sx={{
        // Outer click target — extends past the AccordionSummary's `p: 1`
        // padding via negative vertical margin so the tap area covers the full
        // header height. Margin-box stays at content-area height (no layout
        // shift); only the visible/clickable border-box grows.
        flexShrink: 0,
        ml: 'auto',
        alignSelf: 'stretch',
        my: -1,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        '&:hover': { backgroundColor: 'transparent' },
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <Box
        sx={{
          // Visible pill — preserves the original look (border, height, clip).
          height: { lg: '18px', xs: '16px' },
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          borderRadius: '5px',
          border: `1px solid ${semanticColors.overlayWhite30}`,
          clipPath: 'inset(0 round 5px)',
        }}
      >
        {/* Sliding pill — fixed 50% width, slides via CSS percentage. */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: isPicks ? '0%' : '50%',
            width: '50%',
            borderRadius: '4px',
            backgroundColor: HIGHLIGHT_COLOR,
            transition: 'left 0.25s ease',
            pointerEvents: 'none',
          }}
        />

        {/* Left side — heart only (the aria-label on the ButtonBase still
            announces "your picks" for screen readers, so dropping the visible
            label doesn't lose the semantic). */}
        <Box
          component="span"
          sx={{
            flex: 1,
            position: 'relative',
            zIndex: 1,
            px: 1,
            py: '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isPicks ? semanticColors.textHighEmphasis : semanticColors.overlayWhite85,
          }}
        >
          {isPicks && hasHearts ? (
            <FavoriteIcon sx={{ fontSize: 12, color: primitiveColors.red4 }} />
          ) : (
            <FavoriteBorderIcon sx={{ fontSize: 12 }} />
          )}
        </Box>

        {/* Right side — "All" */}
        <Box
          component="span"
          sx={{
            flex: 1,
            position: 'relative',
            zIndex: 1,
            px: 1,
            py: '2px',
            color: !isPicks ? semanticColors.textHighEmphasis : semanticColors.overlayWhite85,
            fontFamily: 'inherit',
            fontSize: '0.6875rem',
            fontWeight: 500,
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          {t('contests.filterAll')}
        </Box>
      </Box>
    </ButtonBase>
  );
};
